import { formatDateTime } from '@crm/shared';
import { Badge, Button, Card, CardContent, cn, Input, Modal, Spinner } from '@crm/ui';
import {
  ChevronRight,
  CircleCheck,
  Clock,
  Headset,
  LifeBuoy,
  MessagesSquare,
  Plus,
} from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState, type FilterPill, FilterPills, PageHeader, Pagination } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  TICKET_STATUS_LABELS,
  type Ticket,
  type TicketStatus,
  useCreateTicket,
  useTickets,
} from '../api';
import { MessageComposer } from '../components/message-composer';
import { TicketThread } from '../components/ticket-thread';

type Filtro = TicketStatus | 'all';

const FILTROS: FilterPill<Filtro>[] = [
  { key: 'all', label: 'Todos', icon: MessagesSquare },
  { key: 'OPEN', label: 'Abertos', icon: LifeBuoy },
  { key: 'IN_PROGRESS', label: 'Em atendimento', icon: Clock },
  { key: 'RESOLVED', label: 'Resolvidos', icon: CircleCheck },
];

const TOM: Record<TicketStatus, string> = {
  OPEN: 'border-amber-500/50 text-amber-700 dark:text-amber-400',
  IN_PROGRESS: 'border-primary/50 text-primary',
  RESOLVED: 'border-emerald-500/50 text-emerald-700 dark:text-emerald-400',
};

function Linha({ ticket, onOpen }: { ticket: Ticket; onOpen: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-muted/50"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate font-medium">{ticket.subject}</span>
            <Badge variant="outline" className={cn(TOM[ticket.status])}>
              {TICKET_STATUS_LABELS[ticket.status]}
            </Badge>
          </div>
          <p className="truncate text-sm text-muted-foreground">
            {ticket.companyName} - {ticket.messagesTotal} mensagens
            {ticket.lastMessageAt ? ` - ultima em ${formatDateTime(ticket.lastMessageAt)}` : ''}
          </p>
        </div>

        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
    </li>
  );
}

/**
 * Os chamados de suporte. A mesma tela serve os dois lados: o salao ve os
 * proprios e abre chamado; quem administra o SaaS ve todos e responde.
 */
export function SupportPage({ doSaas }: { doSaas?: boolean }) {
  useDocumentTitle('Suporte');

  const [params, setParams] = useSearchParams();
  const [filtro, setFiltro] = useState<Filtro>('all');
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);
  const [abrindo, setAbrindo] = useState(false);
  const [assunto, setAssunto] = useState('');

  const aberto = params.get('chamado');
  const tickets = useTickets({
    status: filtro === 'all' ? '' : filtro,
    search: busca,
    page,
  });
  const criar = useCreateTicket();

  if (aberto) {
    return (
      <div className="space-y-4">
        <PageHeader icon={<Headset />} title="Suporte" />
        <TicketThread id={aberto} doSaas={doSaas} onBack={() => setParams({})} />
      </div>
    );
  }

  const lista = tickets.data?.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        icon={<Headset />}
        title="Suporte"
        description={
          doSaas ? 'Os chamados abertos pelos assinantes.' : 'Fale com quem cuida do sistema.'
        }
        actions={
          doSaas ? null : (
            <Button onClick={() => setAbrindo(true)}>
              <Plus className="h-4 w-4" />
              Novo chamado
            </Button>
          )
        }
      />

      <Input
        placeholder={doSaas ? 'Buscar por assunto ou assinante...' : 'Buscar por assunto...'}
        value={busca}
        onChange={(event) => {
          setBusca(event.target.value);
          setPage(1);
        }}
      />

      <FilterPills
        options={FILTROS}
        value={filtro}
        onChange={(proximo) => {
          setFiltro(proximo);
          setPage(1);
        }}
      />

      <Card variant="flush">
        <CardContent className="py-4">
          {tickets.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : lista.length === 0 ? (
            <EmptyState icon={<Headset />} title="Nenhum chamado por aqui" />
          ) : (
            <ul className="divide-y divide-border">
              {lista.map((ticket) => (
                <Linha
                  key={ticket.id}
                  ticket={ticket}
                  onOpen={() => setParams({ chamado: ticket.id })}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Pagination meta={tickets.data?.meta} label="chamados" onChange={setPage} />

      {abrindo ? (
        <Modal
          open
          onClose={() => setAbrindo(false)}
          title="Novo chamado"
          subtitle="Conte o que aconteceu; imagens ajudam a entender"
          size="medium"
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <Input
                placeholder="Assunto"
                value={assunto}
                onChange={(event) => setAssunto(event.target.value)}
              />
            </div>

            <MessageComposer
              enviando={criar.isPending}
              placeholder="Descreva o problema"
              onSend={(input) =>
                criar.mutate(
                  { subject: assunto.trim() || 'Chamado sem assunto', ...input },
                  {
                    onSuccess: (ticket) => {
                      setAbrindo(false);
                      setAssunto('');
                      setParams({ chamado: ticket.id });
                    },
                  },
                )
              }
            />

            {criar.error ? (
              <p className="text-sm text-destructive">{toErrorMessage(criar.error)}</p>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
